import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineChevronDown, HiOutlineRefresh,
    HiOutlineHome, HiOutlineFolder, HiOutlineFolderAdd, HiOutlineUpload, HiOutlineDownload,
    HiOutlineTrash, HiOutlinePencilAlt, HiOutlineX, HiOutlineDocument,
    HiOutlinePhotograph, HiOutlineFilm, HiOutlineDocumentText, HiOutlineExternalLink,
    HiOutlineSearch, HiOutlineEye, HiOutlineUser
} from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import UserProfileButton from '../common/UserProfileButton';
import LogoutButton from '../common/LogoutButton';
import './FileManager.css';

const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined) return '—';
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (d) => d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const extensionOf = (name) => {
    const dot = (name || '').lastIndexOf('.');
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'heic'];
const VIDEO_EXT = ['mp4', 'mov', 'avi', 'mkv', 'webm'];
const DOC_EXT = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'ppt', 'pptx', 'xls', 'xlsx'];

const ItemIcon = ({ item, size = 20 }) => {
    if (item.isFolder) return <HiOutlineFolder size={size} className="fm-icon-folder" />;
    const ext = extensionOf(item.name);
    if (IMAGE_EXT.includes(ext)) return <HiOutlinePhotograph size={size} className="fm-icon-image" />;
    if (VIDEO_EXT.includes(ext)) return <HiOutlineFilm size={size} className="fm-icon-video" />;
    if (DOC_EXT.includes(ext)) return <HiOutlineDocumentText size={size} className="fm-icon-doc" />;
    return <HiOutlineDocument size={size} className="fm-icon-file" />;
};

// Paths from the server are always relative to whichever folder is being
// browsed, so this never has to reason about who owns it.
const parentOf = (path) => {
    if (!path) return '';
    const parts = path.split('/');
    parts.pop();
    return parts.join('/');
};

// Small centred dialog used for "new folder" and "rename" — both are a single
// text field plus confirm/cancel, so they share one component rather than two
// near-identical modals.
const NameDialog = ({ title, label, initialValue, confirmLabel, busy, onConfirm, onCancel }) => {
    const [value, setValue] = useState(initialValue || '');
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    const submit = (e) => {
        e.preventDefault();
        const trimmed = value.trim();
        if (trimmed) onConfirm(trimmed);
    };

    return (
        <div className="fm-modal-backdrop" onClick={onCancel}>
            <form className="fm-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
                <div className="fm-modal-head">
                    <h3>{title}</h3>
                    <button type="button" className="fm-icon-btn" onClick={onCancel}>
                        <HiOutlineX size={18} />
                    </button>
                </div>
                <label className="fm-modal-label">{label}</label>
                <input
                    ref={inputRef}
                    className="fm-modal-input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
                />
                <div className="fm-modal-actions">
                    <button type="button" className="fm-btn fm-btn-ghost" onClick={onCancel}>Cancel</button>
                    <button type="submit" className="fm-btn fm-btn-primary" disabled={busy || !value.trim()}>
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </form>
        </div>
    );
};

const ConfirmDialog = ({ item, busy, onConfirm, onCancel }) => (
    <div className="fm-modal-backdrop" onClick={onCancel}>
        <div className="fm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fm-modal-head">
                <h3>Delete {item.isFolder ? 'folder' : 'file'}?</h3>
                <button type="button" className="fm-icon-btn" onClick={onCancel}>
                    <HiOutlineX size={18} />
                </button>
            </div>
            <p className="fm-modal-body">
                <strong>{item.name}</strong>
                {item.isFolder && ' and everything inside it'} will be moved to the SharePoint
                recycle bin, where it can be restored for 93 days.
            </p>
            <div className="fm-modal-actions">
                <button type="button" className="fm-btn fm-btn-ghost" onClick={onCancel}>Cancel</button>
                <button type="button" className="fm-btn fm-btn-danger" disabled={busy} onClick={onConfirm}>
                    {busy ? 'Deleting…' : 'Delete'}
                </button>
            </div>
        </div>
    </div>
);

const FileManager = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const userPermissions = user?.permissions || { cards: [], actions: [] };
    const isAdmin = user?.role === 'system_admin';
    // Card actions say what this person may do in their OWN folder. Whether the
    // folder currently open is theirs is a separate question the server answers
    // with `canWrite` — both must hold.
    const mayCreate = isAdmin || hasCardAction(userPermissions, 'file-manager', 'create');
    const mayEdit = isAdmin || hasCardAction(userPermissions, 'file-manager', 'edit');
    const mayDelete = isAdmin || hasCardAction(userPermissions, 'file-manager', 'delete');

    const [path, setPath] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [search, setSearch] = useState('');

    // Whose folder is open. null = my own.
    const [owner, setOwner] = useState(null);
    const [ownerLabel, setOwnerLabel] = useState('');
    const [canWrite, setCanWrite] = useState(true);
    const [people, setPeople] = useState({ scope: 'own', groups: [] });
    const [collapsed, setCollapsed] = useState({});

    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [downloading, setDownloading] = useState({});

    const [newFolderOpen, setNewFolderOpen] = useState(false);
    const [renaming, setRenaming] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [busy, setBusy] = useState(false);

    const fileInputRef = useRef(null);
    const dragDepth = useRef(0);

    const canCreate = mayCreate && canWrite;
    const canEdit = mayEdit && canWrite;
    const canDelete = mayDelete && canWrite;

    const loadFolder = useCallback(async (targetPath, ownerId) => {
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get('/api/drive', {
                params: { path: targetPath || '', owner: ownerId || undefined }
            });
            setItems(res.data.items || []);
            setPath(res.data.path || '');
            setOwner(ownerId || null);
            setCanWrite(res.data.canWrite !== false);
            setOwnerLabel(res.data.owner?.isSelf ? '' : (res.data.owner?.fullName || ''));
        } catch (e) {
            setItems([]);
            setError(e.response?.data?.message || 'Could not open that folder');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadFolder('', null); }, [loadFolder]);

    // Who this person may browse besides themselves. An ordinary user gets an
    // empty list and never sees the sidebar at all.
    useEffect(() => {
        axiosInstance.get('/api/drive/people')
            .then((res) => setPeople({ scope: res.data.scope, groups: res.data.groups || [] }))
            .catch(() => setPeople({ scope: 'own', groups: [] }));
    }, []);

    // Notices are informational ("3 files uploaded") and clear themselves;
    // errors stay until the next action so they can actually be read.
    useEffect(() => {
        if (!notice) return undefined;
        const t = setTimeout(() => setNotice(''), 4000);
        return () => clearTimeout(t);
    }, [notice]);

    const breadcrumbs = useMemo(() => {
        const parts = path ? path.split('/') : [];
        return parts.map((name, i) => ({ name, path: parts.slice(0, i + 1).join('/') }));
    }, [path]);

    const visibleItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) => i.name.toLowerCase().includes(q));
    }, [items, search]);

    const go = (targetPath, ownerId = owner) => {
        setSearch('');
        loadFolder(targetPath, ownerId);
    };

    const openItem = (item) => {
        if (item.isFolder) go(item.path);
        else handleDownload(item);
    };

    const handleUpload = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        setUploadProgress(0);
        setError('');

        const form = new FormData();
        Array.from(files).forEach((f) => form.append('files', f));

        try {
            const res = await axiosInstance.post('/api/drive/files', form, {
                params: { path, owner: owner || undefined },
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
                }
            });
            setNotice(res.data.message);
            loadFolder(path, owner);
        } catch (e) {
            setError(e.response?.data?.message || 'Upload failed');
            // A partial batch still landed some files, so the listing is stale
            // either way — refresh so the user sees exactly what is there.
            loadFolder(path, owner);
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDownload = async (item) => {
        if (downloading[item.path]) return;
        setDownloading((d) => ({ ...d, [item.path]: true }));
        try {
            const res = await axiosInstance.get('/api/drive/content', {
                params: { path: item.path, owner: owner || undefined },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = item.name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setError(`Could not download "${item.name}"`);
        } finally {
            setDownloading((d) => {
                const next = { ...d };
                delete next[item.path];
                return next;
            });
        }
    };

    const handleCreateFolder = async (name) => {
        setBusy(true);
        setError('');
        try {
            await axiosInstance.post('/api/drive/folders', { path, name, owner: owner || undefined });
            setNewFolderOpen(false);
            setNotice(`Folder "${name}" created`);
            loadFolder(path, owner);
        } catch (e) {
            setError(e.response?.data?.message || 'Could not create the folder');
        } finally {
            setBusy(false);
        }
    };

    const handleRename = async (name) => {
        setBusy(true);
        setError('');
        try {
            await axiosInstance.patch('/api/drive/items', {
                path: renaming.path,
                name,
                owner: owner || undefined
            });
            setRenaming(null);
            setNotice('Renamed successfully');
            loadFolder(path, owner);
        } catch (e) {
            setError(e.response?.data?.message || 'Could not rename that item');
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await axiosInstance.delete('/api/drive/items', {
                params: { path: deleting.path, owner: owner || undefined }
            });
            setDeleting(null);
            setNotice(res.data.message);
            loadFolder(path, owner);
        } catch (e) {
            setError(e.response?.data?.message || 'Could not delete that item');
        } finally {
            setBusy(false);
        }
    };

    // Drag counters rather than a plain boolean: dragenter/dragleave fire for
    // every child element, so a single flag flickers off as the pointer crosses
    // a row.
    const onDragEnter = (e) => {
        e.preventDefault();
        if (!canCreate) return;
        dragDepth.current += 1;
        setDragging(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        if (canCreate) handleUpload(e.dataTransfer.files);
    };

    const showSidebar = people.groups.length > 0;

    return (
        <div className="fm-container">
            <header className="fm-header">
                <div className="fm-header-left">
                    <button className="fm-back-btn" onClick={() => navigate('/purpose-selection')}>
                        <HiOutlineChevronLeft size={20} />
                    </button>
                    <div>
                        <h1>My Files</h1>
                        <p>
                            {people.scope === 'all'
                                ? 'Everyone’s folders, grouped by department'
                                : people.scope === 'department'
                                    ? 'Your folder, plus your department’s'
                                    : 'Your private folder — only you can see what is in here'}
                        </p>
                    </div>
                </div>
                <div className="fm-header-right">
                    <UserProfileButton />
                    <LogoutButton />
                </div>
            </header>

            <div className="fm-layout">
                {showSidebar && (
                    <aside className="fm-sidebar">
                        <button
                            className={`fm-person ${owner === null ? 'fm-person-active' : ''}`}
                            onClick={() => go('', null)}
                        >
                            <HiOutlineFolder size={16} className="fm-icon-folder" />
                            <span>My Files</span>
                        </button>

                        {people.groups.map((group) => {
                            const isCollapsed = collapsed[group.department];
                            return (
                                <div className="fm-dept" key={group.department}>
                                    <button
                                        className="fm-dept-head"
                                        onClick={() => setCollapsed((c) => ({
                                            ...c,
                                            [group.department]: !c[group.department]
                                        }))}
                                    >
                                        {isCollapsed
                                            ? <HiOutlineChevronRight size={14} />
                                            : <HiOutlineChevronDown size={14} />}
                                        <span>{group.department}</span>
                                        <span className="fm-dept-count">{group.members.length}</span>
                                    </button>

                                    {!isCollapsed && group.members.map((member) => (
                                        <button
                                            key={member._id}
                                            className={`fm-person ${owner === member._id ? 'fm-person-active' : ''}`}
                                            onClick={() => go('', member._id)}
                                            title={member.fullName}
                                        >
                                            <HiOutlineUser size={15} />
                                            <span>{member.fullName}</span>
                                        </button>
                                    ))}
                                </div>
                            );
                        })}
                    </aside>
                )}

                <div className="fm-main">
                    <div className="fm-toolbar">
                        <nav className="fm-breadcrumbs" aria-label="Folder path">
                            <button
                                className={`fm-crumb ${path ? '' : 'fm-crumb-active'}`}
                                onClick={() => go('')}
                            >
                                <HiOutlineHome size={16} /> {ownerLabel || 'Home'}
                            </button>
                            {breadcrumbs.map((crumb, i) => (
                                <React.Fragment key={crumb.path}>
                                    <HiOutlineChevronRight size={14} className="fm-crumb-sep" />
                                    <button
                                        className={`fm-crumb ${i === breadcrumbs.length - 1 ? 'fm-crumb-active' : ''}`}
                                        onClick={() => go(crumb.path)}
                                    >
                                        {crumb.name}
                                    </button>
                                </React.Fragment>
                            ))}
                        </nav>

                        <div className="fm-actions">
                            <div className="fm-search">
                                <HiOutlineSearch size={16} />
                                <input
                                    placeholder="Filter this folder…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <button className="fm-icon-btn" title="Refresh" onClick={() => loadFolder(path, owner)}>
                                <HiOutlineRefresh size={18} />
                            </button>
                            {canCreate && (
                                <>
                                    <button className="fm-btn fm-btn-ghost" onClick={() => setNewFolderOpen(true)}>
                                        <HiOutlineFolderAdd size={18} /> New folder
                                    </button>
                                    <button
                                        className="fm-btn fm-btn-primary"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                    >
                                        <HiOutlineUpload size={18} />
                                        {uploading ? `Uploading ${uploadProgress}%` : 'Upload'}
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        hidden
                                        onChange={(e) => handleUpload(e.target.files)}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    {/* Says plainly whose folder this is and that it cannot be
                        changed — the buttons are simply absent otherwise, which
                        reads as a bug rather than a rule. */}
                    {!canWrite && ownerLabel && (
                        <div className="fm-alert fm-alert-info">
                            <HiOutlineEye size={16} />
                            Viewing <strong>{ownerLabel}</strong>’s files — read-only. You can open and
                            download, but only {ownerLabel.split(' ')[0]} can change what is in here.
                        </div>
                    )}

                    {uploading && (
                        <div className="fm-progress">
                            <div className="fm-progress-bar" style={{ width: `${uploadProgress}%` }} />
                        </div>
                    )}

                    {error && <div className="fm-alert fm-alert-error">{error}</div>}
                    {notice && <div className="fm-alert fm-alert-ok">{notice}</div>}

                    <div
                        className={`fm-body ${dragging ? 'fm-dragging' : ''}`}
                        onDragEnter={onDragEnter}
                        onDragOver={(e) => e.preventDefault()}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                    >
                        {dragging && (
                            <div className="fm-drop-overlay">
                                <HiOutlineUpload size={40} />
                                <span>Drop files to upload here</span>
                            </div>
                        )}

                        {loading ? (
                            <div className="fm-empty">Loading…</div>
                        ) : visibleItems.length === 0 ? (
                            <div className="fm-empty">
                                <HiOutlineFolder size={48} />
                                {search
                                    ? <span>Nothing here matches “{search}”.</span>
                                    : items.length === 0
                                        ? <span>
                                            This folder is empty.
                                            {canCreate && ' Drag files in, or use Upload.'}
                                        </span>
                                        : null}
                            </div>
                        ) : (
                            <table className="fm-table">
                                <thead>
                                    <tr>
                                        <th className="fm-col-name">Name</th>
                                        <th className="fm-col-size">Size</th>
                                        <th className="fm-col-date">Modified</th>
                                        <th className="fm-col-actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleItems.map((item) => (
                                        <tr
                                            key={item.itemId}
                                            className="fm-row"
                                            onDoubleClick={() => openItem(item)}
                                        >
                                            <td className="fm-col-name">
                                                <button className="fm-name-btn" onClick={() => openItem(item)}>
                                                    <ItemIcon item={item} />
                                                    <span className="fm-name-text">{item.name}</span>
                                                </button>
                                            </td>
                                            <td className="fm-col-size">
                                                {item.isFolder
                                                    ? `${item.childCount ?? 0} item${item.childCount === 1 ? '' : 's'}`
                                                    : formatBytes(item.size)}
                                            </td>
                                            <td className="fm-col-date">{formatDate(item.modifiedAt)}</td>
                                            <td className="fm-col-actions">
                                                {!item.isFolder && (
                                                    <button
                                                        className="fm-icon-btn"
                                                        title="Download"
                                                        disabled={!!downloading[item.path]}
                                                        onClick={() => handleDownload(item)}
                                                    >
                                                        <HiOutlineDownload size={16} />
                                                    </button>
                                                )}
                                                {item.webUrl && (
                                                    // Only useful to people who are also members of the
                                                    // SharePoint site; everyone else uses the buttons above,
                                                    // which stream through the backend under their card.
                                                    <a
                                                        className="fm-icon-btn"
                                                        href={item.webUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Open in SharePoint"
                                                    >
                                                        <HiOutlineExternalLink size={16} />
                                                    </a>
                                                )}
                                                {canEdit && (
                                                    <button
                                                        className="fm-icon-btn"
                                                        title="Rename"
                                                        onClick={() => setRenaming(item)}
                                                    >
                                                        <HiOutlinePencilAlt size={16} />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        className="fm-icon-btn fm-icon-danger"
                                                        title="Delete"
                                                        onClick={() => setDeleting(item)}
                                                    >
                                                        <HiOutlineTrash size={16} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {path && (
                        <button className="fm-up-btn" onClick={() => go(parentOf(path))}>
                            <HiOutlineChevronLeft size={16} />
                            Back to {parentOf(path) ? parentOf(path).split('/').pop() : (ownerLabel || 'Home')}
                        </button>
                    )}
                </div>
            </div>

            {newFolderOpen && (
                <NameDialog
                    title="New folder"
                    label="Folder name"
                    confirmLabel="Create"
                    busy={busy}
                    onConfirm={handleCreateFolder}
                    onCancel={() => setNewFolderOpen(false)}
                />
            )}

            {renaming && (
                <NameDialog
                    title={`Rename ${renaming.isFolder ? 'folder' : 'file'}`}
                    label="New name"
                    initialValue={renaming.name}
                    confirmLabel="Rename"
                    busy={busy}
                    onConfirm={handleRename}
                    onCancel={() => setRenaming(null)}
                />
            )}

            {deleting && (
                <ConfirmDialog
                    item={deleting}
                    busy={busy}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleting(null)}
                />
            )}
        </div>
    );
};

export default FileManager;
